import * as React from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Command as CommandPrimitive, defaultFilter } from 'cmdk';
import {
  ArrowDownIcon,
  ArrowRightToLineIcon,
  ArrowUpIcon,
  CornerDownLeftIcon,
  SearchIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const MIN_SCORE_THRESHOLD = 0.15;

export const COMMAND_ITEM_CLASS_NAME =
  "relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-selected-soft data-[selected=true]:text-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground";

function Command({ className, ...props }: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      filter={(value, search, keywords) => {
        const score = defaultFilter(value, search, keywords);
        return score > MIN_SCORE_THRESHOLD ? score : 0;
      }}
      className={cn(
        'flex h-full w-full flex-col overflow-hidden rounded-md bg-background text-foreground',
        className
      )}
      {...props}
    />
  );
}

function CommandDialog({
  title = 'Command Palette',
  description = 'Search for a command to run…',
  children,
  ...props
}: React.ComponentProps<typeof Dialog> & { title?: string; description?: string }) {
  const { t } = useTranslation();

  return (
    <Dialog {...props}>
      <DialogHeader className="sr-only">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogPortal>
        <DialogOverlay className="bg-black/15" />
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className="fixed top-[20%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] gap-4 overflow-hidden rounded-lg border bg-background p-0 shadow-lg duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:max-w-lg"
        >
          <Command
            className={cn(
              '[&_[cmdk-input]]:h-12 [&_[data-slot=command-input-wrapper]]:h-12',
              '[&_[cmdk-input-wrapper]_svg]:size-5',
              '[&_[cmdk-group]]:px-2 [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0',
              '[&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:size-5'
            )}
          >
            {children}
          </Command>
          <div className="flex items-center justify-between border-t px-3 py-2.5 text-xs text-muted-foreground">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <kbd className="inline-flex size-4 items-center justify-center rounded border bg-background shadow-sm">
                  <CornerDownLeftIcon className="size-2" />
                </kbd>
                {t('command_palette.shortcut_open')}
              </span>
              <span className="flex items-center gap-1.5">
                <kbd className="inline-flex size-4 items-center justify-center rounded border bg-background text-[6px] font-medium shadow-sm">
                  Esc
                </kbd>
                {t('command_palette.shortcut_close')}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <kbd className="inline-flex size-4 items-center justify-center rounded border bg-background shadow-sm">
                <ArrowRightToLineIcon className="size-2" />
              </kbd>
              <kbd className="inline-flex size-4 items-center justify-center rounded border bg-background shadow-sm">
                <ArrowUpIcon className="size-2" />
              </kbd>
              <kbd className="inline-flex size-4 items-center justify-center rounded border bg-background shadow-sm">
                <ArrowDownIcon className="size-2" />
              </kbd>
              {t('command_palette.shortcut_navigate')}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

function CommandInput({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div data-slot="command-input-wrapper" className="flex h-9 items-center gap-2 border-b px-3">
      <SearchIcon className="size-4 shrink-0 text-muted-foreground" />
      <CommandPrimitive.Input
        data-slot="command-input"
        className={cn(
          'flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        {...props}
      />
    </div>
  );
}

function CommandList({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn('max-h-96 scroll-py-1 overflow-x-hidden overflow-y-auto', className)}
      {...props}
    />
  );
}

function CommandEmpty({ ...props }: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className="py-6 text-center text-sm"
      {...props}
    />
  );
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        'overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground',
        className
      )}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn('-mx-1 h-px bg-border', className)}
      {...props}
    />
  );
}

function CommandItem({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(COMMAND_ITEM_CLASS_NAME, className)}
      {...props}
    />
  );
}

function CommandShortcut({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn('ml-auto text-xs tracking-widest text-muted-foreground', className)}
      {...props}
    />
  );
}

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
